import React, { ReactNode, useEffect, useMemo } from "react";
import { TextButton } from "@/components/common/text-button";
import { SpendCreditIcon } from "@/components/ui/icon";
import {
  SPEND_CREDITS_EVENT,
  subscriptionHook,
} from "@/manager/manager/subscription-manager";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

/** 消耗积分事件值（与 SPEND_CREDITS_EVENT 对齐） */
export type SpendCreditsEvent =
  (typeof SPEND_CREDITS_EVENT)[keyof typeof SPEND_CREDITS_EVENT];

export interface CreditConsumeButtonProps {
  className?: string;
  /** 按钮文案（图标/积分数会自动追加在右侧） */
  label: ReactNode;
  /** 消耗积分事件，传入后请求积分预估接口 */
  event: SpendCreditsEvent;
  /** 预估的事件数量，默认 1 */
  count?: number;
  /** 是否在文案旁展示预估积分，默认 true（仅在 spendCredits > 0 时实际展示） */
  showSpendCredits?: boolean;
  /** 是否检查积分是否足够，默认 true */
  checkCredits?: boolean;
  disabled?: boolean;
  loading?: boolean;
  /** 点击触发的回调 */
  onClick?: (data: { spendCredits: number }) => void;
}

/**
 * 积分消耗按钮
 * - 传入 `event` 后，组件调用积分预估接口，并在按钮文案右侧展示预估积分。
 * - `checkCredits` 点击事件的积分检查，默认开启
 */
export const CreditConsumeButton: React.FC<CreditConsumeButtonProps> = ({
  className,
  label,
  event,
  count = 1,
  showSpendCredits = true,
  checkCredits = true,
  disabled,
  loading,
  onClick,
}) => {
  const toast = useToast();
  const { t } = useTranslation();
  const { spendCredits, debounceEstimate } = subscriptionHook.useFeatureEstimate();
  const { hasEnoughCredits } = subscriptionHook.useCheckCredits();

  // event/count 变化时触发积分预估
  useEffect(() => {
    if (!event) return;
    debounceEstimate({ event, count });
  }, [event, count]);

  // 是否需要在文案右侧渲染消耗积分
  const shouldRenderCredits = showSpendCredits && spendCredits > 0;

  const buttonLabel = useMemo<ReactNode>(() => {
    if (!shouldRenderCredits) return label;
    return (
      <div className="flex items-center gap-1">
        <span>{label}</span>
        <div className="flex items-center">
          <SpendCreditIcon className="mr-1" />
          <span className="font-medium">{spendCredits}</span>
        </div>
      </div>
    );
  }, [label, shouldRenderCredits, spendCredits]);

  const handleClick = () => {
    // 检查积分是否足够
    if (checkCredits && showSpendCredits) {
      if (spendCredits <= 0) return;
      if (!hasEnoughCredits(spendCredits)) {
        toast.toast({ description: t('status_code_messages.1023') })
        return;
      };
    }
    // 点击触发回调
    onClick?.({ spendCredits });
  }

  return (
    <TextButton
      className={className}
      label={buttonLabel}
      loading={loading}
      disabled={Boolean(disabled || loading)}
      onClick={handleClick}
    />
  );
};
